import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  Client, ClientListResponse, ClientListParams, ClientInput,
} from '@/lib/types';

export async function fetchClients(params: ClientListParams): Promise<ClientListResponse> {
  const { data } = await apiClient.get<ClientListResponse>('/api/v1/clients', { params });
  return data;
}

export async function fetchClient(id: string): Promise<Client> {
  const { data } = await apiClient.get<{ client: Client }>(`/api/v1/clients/${id}`);
  return data.client;
}

export async function createClient(input: ClientInput): Promise<Client> {
  const { data } = await apiClient.post<{ client: Client }>('/api/v1/clients', input);
  return data.client;
}

export async function updateClient(id: string, input: Partial<ClientInput>): Promise<Client> {
  const { data } = await apiClient.patch<{ client: Client }>(`/api/v1/clients/${id}`, input);
  return data.client;
}

export function useClients(params: ClientListParams) {
  return useQuery({
    queryKey: ['clients', params],
    queryFn: () => fetchClients(params),
    placeholderData: (prev) => prev,
  });
}

export function useClient(id: string | null | undefined) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: () => fetchClient(id!),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createClient,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ClientInput> }) =>
      updateClient(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.setQueryData(['client', updated.id], updated);
    },
  });
}
